import React, { useEffect, Suspense } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { GetReportsList } from '@store/NTX/Reports/reportSlice';
import ReportsAllByModule from '../../NTX/Reports/ReportsModule/Index';
import { v4 as uuidv4 } from 'uuid';

// استيراد المكونات باستخدام React.lazy
const StockCardReport = React.lazy(() => import('./StockCardReport'));
const StockBalanceReport = React.lazy(() => import('./StockBalanceReport'));
const StockAgingReport = React.lazy(() => import('./StockAgingReport'));
const WarehouseTransferReport = React.lazy(() => import('./WarehouseTransferReport'));
const CostVsSalesReport = React.lazy(() => import('./CostVsSalesReport'));
const ReorderPointReport = React.lazy(() => import('./ReorderPointReport'));
const InventoryTurnoverReport = React.lazy(() => import('./InventoryTurnoverReport'));
const PrintBarcode = React.lazy(() => import('./BarcodePrint2/PriceListBarcodePrint'));
const StockCardTransactionsReport = React.lazy(() => import('./StockCardTransactionsReport'));
// Paged
const RPT_InventoryValuation = React.lazy(() => import('../InvReports_Paged/RPT_InventoryValuation'));

const RPT_InventoryTransferReport = React.lazy(() => import('../InvReports_Paged/InventoryTransferReport_Paged'));
interface Report {
  id: string;
  name: string;
  name2: string;
  moduleId: string;
  tableName: string;
  query: string;
  description: string;
  pageType: string;
  reportType: string;
}

interface ReportsListState {
  ReportsList: Report[];
  loading: boolean;
}

//________________ Start Components
const InventoryReports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { ReportsList, loading } = useAppSelector(state => state.Reports) as ReportsListState;
  useEffect(() => {
    dispatch(GetReportsList());
  }, [dispatch]);

  const reportListByModule = ReportsList?.filter(
    f => f.moduleId?.toLowerCase() === '0a6c6b89-1d52-4eaa-8d2c-070c74b1f6e0'
  );

  const InvReports = [
    // العناصر القادمة من النظام (تقارير مخصصة)
    ...(reportListByModule ?? []).map(report => ({
      ...report,
      type: 'custom',
      component: null // لا يوجد مكون ثابت
    })),

    // تقارير ثابتة إضافية
    {
      id: uuidv4(),
      name: 'Print Barcode',
      name2: 'طباعة الباركــود',
      icon: 'fas fa-boxes',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <PrintBarcode />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'Stock Card Report',
      name2: ' بطاقة المخزون',
      icon: 'fas fa-boxes',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <StockCardReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'Stock Card Transactions',
      name2: ' حركات بطاقة المخزون',
      icon: 'fas fa-exchange-alt',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <StockCardTransactionsReport />
        </Suspense>
      )
    },

    {
      id: uuidv4(),
      name: 'StockBalanceReport',
      name2: ' رصيد المخزون',
      icon: 'fas fa-boxes',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <StockBalanceReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'InventoryAgingReport',
      name2: ' أعمار المخزون',
      icon: 'fas fa-hourglass-half',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <StockAgingReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'WarehouseTransferReport',
      name2: ' تحويلات المخازن',
      icon: 'fas fa-random',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <WarehouseTransferReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'CostVsSalesReport',
      name2: ' تكلفة مقابل مبيعات',
      icon: 'fas fa-balance-scale',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <CostVsSalesReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'ReorderPointReport',
      name2: ' نقطة إعادة الطلب',
      icon: 'fas fa-bell',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <ReorderPointReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'InventoryTurnoverReport',
      name2: ' دوران المخزون',
      icon: 'fas fa-sync-alt',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <InventoryTurnoverReport />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'Inventory Valuation ',
      name2: ' تقييم المخزون',
      icon: 'fas fa-warehouse',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <RPT_InventoryValuation />
        </Suspense>
      )
    },
    {
      id: uuidv4(),
      name: 'Inventory Transfer Report',
      name2: 'تقرير التحويلات المخزنية',
      icon: 'fas fa-exchange-alt',
      type: 'static',
      component: (
        <Suspense fallback={<LazyLoading />}>
          <RPT_InventoryTransferReport />
        </Suspense>
      )
    }
  ];

  return (
    <>
      <ReportsAllByModule ReportsList={InvReports} loading={loading} reportType='both' />
    </>
  );
};

export default InventoryReports;

const LazyLoading = () => {
  return (
    <div role='status' className='d-flex justify-content-center align-items-center py-5'>
      <span className='spinner-border spinner-border-sm text-theme me-2' aria-hidden='true'></span>
      <span className='visually-hidden'>جاري التحميل...</span>
    </div>
  );
};
